import React, { useEffect, useRef, useState } from 'react';
import { CircleHelp, Download, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import { downloadParticipantsTemplate } from '../../../utils/participantsTemplate';
import { useLanguage } from '../../../assets/js/LanguageContext';
import AppModal from '../../common/Modal';
import FormField from '../../common/FormField';
import { usePlanLimits } from '../../../utils/planLimits';
import '../../../assets/styles/TemplateBuilder.css';

const todayValue = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const formatQuestions = (selectedQuestions) => selectedQuestions.map((q) => {
  const base = { textoPregunta: q.question, opciones: null, minValue: null, maxValue: null };

  if (q.type === 'closed') {
    return { ...base, tipoPregunta: 'closed', opciones: q.options ? q.options.join(',') : null };
  }
  if (q.type === 'rating') {
    return { ...base, tipoPregunta: 'rating', minValue: q.minValue, maxValue: q.maxValue };
  }
  return { ...base, tipoPregunta: 'open' };
});

const CreateSurveyModal = ({ onClose, onSubmit, selectedQuestions = [], initialTopic = '', limitError = null }) => {
  const { t } = useLanguage();
  const today = todayValue();
  const [surveyName, setSurveyName] = useState(initialTopic);
  const [emails, setEmails] = useState([]);
  const [telefonos, setTelefonos] = useState([]);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState('');
  const [errors, setErrors] = useState({});
  const [showInfo, setShowInfo] = useState(false);
  const fileInputRef = useRef(null);
  const checkPlanLimits = usePlanLimits();

  useEffect(() => {
    if (initialTopic) setSurveyName(initialTopic);
  }, [initialTopic]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (rows.length === 0) {
        setErrors((prev) => ({ ...prev, file: t('excel_empty_file') }));
        return;
      }

      const headers = rows[0] || [];
      const emailCol = headers.findIndex((h) => typeof h === 'string' && h.toLowerCase() === 'email');
      const phoneCol = headers.findIndex((h) => typeof h === 'string' && h.toLowerCase() === 'telefono');

      if (emailCol === -1 && phoneCol === -1) {
        setErrors((prev) => ({ ...prev, file: t('excel_missing_email_phone_column') }));
        return;
      }

      const nextEmails = [];
      const nextPhones = [];
      rows.slice(1).forEach((row) => {
        if (!row) return;
        if (emailCol !== -1 && row[emailCol]) nextEmails.push(String(row[emailCol]).trim());
        if (phoneCol !== -1 && row[phoneCol]) nextPhones.push(String(row[phoneCol]).trim());
      });

      setErrors((prev) => ({ ...prev, limit: undefined }));
      setEmails(nextEmails);
      setTelefonos(nextPhones);
      setErrors((prev) => ({ ...prev, file: undefined }));
    } catch (error) {
      console.error('Error al leer el archivo de participantes:', error);
      setErrors((prev) => ({ ...prev, file: t('excel_no_worksheets') }));
    } finally {
      event.target.value = '';
    }
  };

  const handleSubmit = () => {
    const nextErrors = {};

    if (!surveyName.trim()) nextErrors.name = t('create_survey_name_required');

    if (!startDate) nextErrors.start = t('create_start_date_required');
    else if (startDate < today) nextErrors.start = t('create_start_date_past');

    if (!endDate) nextErrors.end = t('create_end_date_required');
    else if (startDate && endDate < startDate) nextErrors.end = t('create_end_date_before_start');

    setErrors((prev) => ({ ...prev, ...nextErrors, name: nextErrors.name, start: nextErrors.start, end: nextErrors.end }));

    if (nextErrors.name || nextErrors.start || nextErrors.end) return false;

    const participantCount = Math.max(emails.length, telefonos.length);
    const exceeded = checkPlanLimits(participantCount);
    if (exceeded) {
      const limitText = exceeded.maxParticipantes != null
        ? t('limit_participants_text').replace('{plan}', exceeded.plan || '').replace('{max}', exceeded.maxParticipantes).replace('{count}', participantCount)
        : t('limit_surveys_text').replace('{plan}', exceeded.plan || '').replace('{limit}', exceeded.limite ?? '—');
      setErrors((prev) => ({ ...prev, limit: limitText }));
      return false;
    }

    onSubmit({
      nombreEncuesta: surveyName,
      imagenEncuesta: null,
      preguntas: formatQuestions(selectedQuestions),
      participantes: { emails, telefonos },
      fechaInicio: startDate,
      fechaFin: endDate,
    });

    return false;
  };

  const limitMessage = limitError && (limitError.error || limitError.message);

  return (
    <>
      <AppModal
        open
        onOpenChange={(open) => { if (!open) onClose(); }}
        variant="edit"
        title={t('create_survey_modal_title')}
        description={t(selectedQuestions.length === 1 ? 'create_survey_modal_lead_one' : 'create_survey_modal_lead').replace('{count}', selectedQuestions.length)}
        cancelText={t('common_cancel')}
        confirmText={t('create_survey_submit')}
        onCancel={onClose}
        onConfirm={handleSubmit}
        size="md"
      >
        <div className="tp-form tp-form--bare tp-setup tp-setup__form">
          <FormField
            id="new-survey-name"
            label={t('create_diagnostic_name')}
            type="text"
            value={surveyName}
            placeholder={t('create_name_placeholder')}
            onChange={(event) => { setSurveyName(event.target.value); setErrors((prev) => ({ ...prev, name: undefined })); }}
            error={errors.name}
          />

          <div className="tp-setup__dates">
            <FormField
              id="new-survey-start"
              label={t('create_start_date')}
              type="date"
              value={startDate}
              min={today}
              onChange={(event) => { setStartDate(event.target.value); setErrors((prev) => ({ ...prev, start: undefined, end: undefined })); }}
              error={errors.start}
              hint={t('setup_start_hint')}
            />
            <FormField
              id="new-survey-end"
              label={t('create_end_date')}
              type="date"
              value={endDate}
              min={startDate || today}
              onChange={(event) => { setEndDate(event.target.value); setErrors((prev) => ({ ...prev, end: undefined })); }}
              error={errors.end}
            />
          </div>

          <section className="tp-setup__participants">
            <div className="tp-setup__participants-head">
              <h3 className="tp-setup__subtitle">{t('create_participants')}</h3>
              <button type="button" className="tp-setup__info" onClick={() => setShowInfo(true)}>
                <CircleHelp size={15} strokeWidth={2} aria-hidden="true" />
                {t('setup_anonymity_link')}
              </button>
            </div>

            <div className="tp-setup__import">
              <button type="button" className="btn btn--outline btn--sm" onClick={() => fileInputRef.current.click()}>
                <FileSpreadsheet size={15} strokeWidth={2} aria-hidden="true" />
                {t('create_import_participants')}
              </button>
              <button type="button" className="btn btn--ghost btn--sm tp-setup__template" onClick={() => downloadParticipantsTemplate(t)}>
                <Download size={15} strokeWidth={2} aria-hidden="true" />
                {t('participants_template_download')}
              </button>
              {Math.max(emails.length, telefonos.length) > 0 && (
                <span className="tp-setup__count">
                  {t('setup_participants_loaded').replace('{count}', Math.max(emails.length, telefonos.length))}
                </span>
              )}
            </div>

            {errors.file && <p className="tp-form__alert" role="alert">{errors.file}</p>}

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".xlsx, .xls"
              className="tp-sr-only"
              tabIndex={-1}
            />
          </section>

          {(errors.limit || limitMessage) && <p className="tp-form__alert" role="alert">{errors.limit || limitMessage}</p>}
        </div>
      </AppModal>

      <AppModal
        open={showInfo}
        onOpenChange={setShowInfo}
        variant="info"
        title={t('create_anonymity_title')}
        description={t('create_anonymity_text')}
        confirmText={t('common_close')}
      />
    </>
  );
};

export default CreateSurveyModal;
